import React, { useState, useEffect } from 'react';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { globalCss as global } from '@uhg-abyss/web/tools/styled';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { SideNavV3 } from './SideNavV3';
import { PageHeadingV3 } from './PageHeadingV3';
import { APIDetailsV3 } from './APIDetailsV3';
import { ErrorCodesV3 } from './ErrorCodesV3';
import { useDocumentationV3 } from '@src/hooks/useDocumentationV3';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { event } from '@uhg-abyss/web/tools/event';
import { useSelector } from 'react-redux';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { LagoonContext } from '@src/AppContext';
import { ServiceSandboxV3 } from '@src/common/ServiceSandboxV3';

const UpdatedSandboxStyle = global({
  '.abyss-table-cell': {
    wordBreak: 'unset !important',
  },
  '.abyss-box-root': {
    alignItems: 'flex-start',
  },
});

const StyledContent = (props) => {
  return (
    <Grid.Col
      css={{
        'abyss-grid-col': {
          paddingLeft: '0px !important',
          paddingRight: '0px !important',
        },
      }}
      {...props}
    />
  );
};

const INITIAL_CURRENT_API = {
  description: '',
  isDisabled: false,
  method: '',
  operationId: '',
  operationSlug: '',
  parameters: [],
  path: '',
  produces: [],
  responses: [],
  summary: '',
  tags: [],
  schema: {},
};

export const DocumentationV3 = () => {
  const lagoonContext = React.useContext(LagoonContext);
  const [sideNavData, setSideNavData] = useState([]);
  const [isMarkDown, setIsMarkDown] = useState(false);

  const [currentApi, setCurrentApi] = useState(INITIAL_CURRENT_API);

  const { navigate } = useRouter();
  const { service, slug } = useParams();

  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });

  const handleTabChange = (tab) => {
    const interactionName = `${service} ${currentApi.method} ${currentApi.summary}`;
    const interactionValueList = ['api sandbox', 'api details', 'error codes'];
    const interactionValue = interactionValueList[tab];
    const interactionContext = 'tab clicks';
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
  };

  const getCurrentApi = (data, slugParm) => {
    let returnVal;
    data.pathsByTag.forEach((listByTag) => {
      listByTag.paths.forEach((path) => {
        if (path.operationSlug.toLowerCase() === slugParm?.toLowerCase()) {
          returnVal = path;
        }
      });
    });

    if (returnVal) {
      setCurrentApi({ ...returnVal, schema: data?.components?.schemas || {} });
      if (slugParm) {
        event('DOCUMENTATION_PAGE_LOAD', {
          service,
          slug: slugParm,
          dsUserInfo,
          userInfo,
        });
      }
    }
  };

  useEffect(() => {
    getCurrentApi(data, slug);
  }, [slug]);

  const handleCompleted = React.useCallback(
    (data) => {
      if (data.pathsByTag && data.pathsByTag[0]) {
        const returnVal = { ...data.pathsByTag[0].paths[0] };
        getCurrentApi(data, returnVal.operationSlug || slug);
      }
    },
    [slug, service]
  );

  const [documentation, getDocumentation] = useDocumentationV3({
    onCompleted: handleCompleted,
    onError: () => {
      setCurrentApi(INITIAL_CURRENT_API);
    },
  });

  useEffect(() => {
    if (service && lagoonContext) {
      const navData = lagoonContext
        ?.getTable('technical-documentation')
        .getRow();
      const filteredNavData = navData.filter((item) => {
        return item.active;
      });
      if (filteredNavData && filteredNavData.length > 0) {
        if (slug === undefined && filteredNavData[0].id) {
          navigate(`/documentation/${service}/${filteredNavData[0].id}`);
        }
        setSideNavData(filteredNavData);

        const currentNavItem = filteredNavData.find((item) => {
          return item.id === slug;
        });
        if (currentNavItem) {
          setIsMarkDown(currentNavItem.markdown || false);
        }
      }
    }
  }, [service, slug, lagoonContext, navigate]);

  const { data } = documentation;

  useEffect(() => {
    if (service && slug !== undefined) {
      // Reset state to initial values
      setCurrentApi(INITIAL_CURRENT_API);
      // Fetch new documentation data
      const serviceUrl =
        service === 'superconnector' ? `${service}/${slug}` : service;
      getDocumentation({ userInfo, service: serviceUrl });
      //getDocumentation({ userInfo, service });
    }
  }, [slug, service]);

  return (
    <LoadingOverlay
      label="superconnector"
      isLoading={documentation.loading}
      isError={documentation.error}
    >
      <Grid>
        <StyledContent span={3}>
          <SideNavV3>
            <SideNavV3.Link
              service={service}
              data={sideNavData}
              currentTags={currentApi.tags}
            />
          </SideNavV3>
        </StyledContent>
        <StyledContent span={9}>
          <div>
            <PageHeadingV3
              info={data?.info}
              currentApi={currentApi}
              service={service}
              slug={slug}
            />
            <Tabs
              onTabChange={handleTabChange}
              css={{
                '.abyss-tabs-tab-content': { fontSize: '15px' },
              }}
            >
              <Tabs.Tab label="API Sandbox">
                <UpdatedSandboxStyle />
                <ServiceSandboxV3 currentApi={currentApi} swagger={data} />
              </Tabs.Tab>
              <Tabs.Tab label="API Details">
                <APIDetailsV3 currentApi={currentApi} isMarkdown={isMarkDown} />
              </Tabs.Tab>
              <Tabs.Tab label="Error Codes">
                <ErrorCodesV3 currentApi={currentApi} />
              </Tabs.Tab>
            </Tabs>
          </div>
        </StyledContent>
      </Grid>
    </LoadingOverlay>
  );
};
